import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { getErrorObjFormat } from "../../../helpers/errorObjFormat";

const url = process.env.REACT_APP_API_BASE_URL + "/v1/rates";

const ratesInitialState = {
  ratesList: [],
  rateshopper: [],
  currentPageRate: {},
  isLoading: false,
  error: null,
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function startLoading(state) {
  state.isLoading = true;
}
function loadingFailed(state, { payload }) {
  state.isLoading = false;
  state.error = payload;
}

const rates = createSlice({
  name: "rates",
  initialState: ratesInitialState,
  reducers: {
    getRateStart: startLoading,
    getRatesStart: startLoading,
    createRateStart: startLoading,
    editRateStart: startLoading,
    deleteRateStart: startLoading,
    getRateshopperStart: startLoading,
    syncRateshopperStart: startLoading,
    getRateSuccess(state, { payload }) {
      state.currentPageRate = payload;
      state.isLoading = false;
      state.error = null;
    },
    getRatesSuccess(state, { payload }) {
      state.ratesList = payload;
      state.isLoading = false;
      state.error = null;
    },
    createRateSuccess(state) {
      state.isLoading = false;
      state.error = null;
    },
    editRateSuccess(state) {
      state.isLoading = false;
      state.error = null;
    },
    deleteRateSuccess(state) {
      state.isLoading = false;
      state.error = null;
    },
    getRateshopperSuccess(state, { payload }) {
      state.rateshopper = payload.rateshopper;
      state.isLoading = false;
      state.error = null;
    },
    syncRateshopperSuccess(state, { payload }) {
      state.isLoading = false;
      state.error = null;
    },
    getRateFailure: loadingFailed,
    getRatesFailure: loadingFailed,
    createRateFailure: loadingFailed,
    editRateFailure: loadingFailed,
    deleteRateFailure: loadingFailed,
    getRateshopperFailure: loadingFailed,
    syncRateshopperFailure: loadingFailed,
  },
});

export const {
  getRateStart,
  getRatesStart,
  getRateshopperStart,
  syncRateshopperStart,
  createRateStart,
  editRateStart,
  deleteRateStart,
  getRateSuccess,
  getRatesSuccess,
  getRateshopperSuccess,
  syncRateshopperSuccess,
  createRateSuccess,
  editRateSuccess,
  deleteRateSuccess,
  getRateFailure,
  getRatesFailure,
  getRateshopperFailure,
  syncRateshopperFailure,
  createRateFailure,
  editRateFailure,
  deleteRateFailure,
} = rates.actions;

export default rates.reducer;

export const fetchRates = () => async (dispatch, getState) => {
  try {
    dispatch(getRatesStart());
    const res = await axios.get(url, {
      headers: { Authorization: `Bearer ${getState().auth.token}` },
    });
    dispatch(getRatesSuccess(res.data.response));
  } catch (err) {
    let errorObj = getErrorObjFormat(err);
    dispatch(getRatesFailure(errorObj));
  }
};

export const fetchRate = (id) => async (dispatch, getState) => {
  try {
    dispatch(getRateStart());
    const res = await axios.get(`${url}/${id}`, {
      headers: { Authorization: `Bearer ${getState().auth.token}` },
    });
    dispatch(getRateSuccess(res.data.response));
  } catch (err) {
    let errorObj = getErrorObjFormat(err);
    dispatch(getRateFailure(errorObj));
  }
};

export const fetchRatesForDates =
  (checkIn, checkOut, hotelId, roomId = null, dayType = "day") =>
  async (dispatch, getState) => {
    try {
      dispatch(getRatesStart());
      // Custom week/month rates live in their own rows, regular day rates don't
      const customRates = dayType === "day" ? 0 : 1;
      const res = await axios.get(
        `${url}/${checkIn}/${checkOut}/${hotelId}/${roomId ? roomId : ""}`,
        {
          headers: { Authorization: `Bearer ${getState().auth.token}` },
          params: { day_type: dayType, custom_rates: customRates },
        }
      );
      dispatch(getRatesSuccess(res.data.response));
      return { error: false, response: res.data.response };
    } catch (err) {
      let errorObj = getErrorObjFormat(err);
      dispatch(getRatesFailure(errorObj));
      return { error: true, ...errorObj };
    }
  };

export const fetchRatesForMonth =
  (date, hotelId, roomId) => async (dispatch, getState) => {
    try {
      dispatch(getRatesStart());
      const res = await axios.get(`${url}/month/${date}/${hotelId}/${roomId}`, {
        headers: { Authorization: `Bearer ${getState().auth.token}` },
      });
      dispatch(getRatesSuccess(res.data.response));
    } catch (err) {
      let errorObj = getErrorObjFormat(err);
      dispatch(getRatesFailure(errorObj));
    }
  };

export const createRate = (rateObj) => async (dispatch, getState) => {
  try {
    dispatch(createRateStart());
    const res = await axios.post(url, rateObj, {
      headers: { Authorization: `Bearer ${getState().auth.token}` },
    });
    dispatch(createRateSuccess());
    return { error: false, id: res.data.response.insertId };
  } catch (err) {
    let errorObj = getErrorObjFormat(err);
    dispatch(createRateFailure(errorObj));
    return { error: true, ...errorObj };
  }
};

export const editRate = (rateId, rateObj) => async (dispatch, getState) => {
  try {
    dispatch(editRateStart());
    const res = await axios.put(`${url}/${rateId}`, rateObj, {
      headers: { Authorization: `Bearer ${getState().auth.token}` },
    });
    dispatch(editRateSuccess());
    return { error: false, id: res.data.response.id };
  } catch (err) {
    let errorObj = getErrorObjFormat(err);
    dispatch(editRateFailure(errorObj));
    return { error: true, ...errorObj };
  }
};
export const editRatesByMonthForHotelRoom =
  (rateObj) => async (dispatch, getState) => {
    try {
      dispatch(editRateStart());
      const res = await axios.put(`${url}`, rateObj, {
        headers: { Authorization: `Bearer ${getState().auth.token}` },
      });
      dispatch(editRateSuccess());
      return { error: false, res };
    } catch (err) {
      let errorObj = getErrorObjFormat(err);
      dispatch(editRateFailure(errorObj));
      return { error: true, ...errorObj };
    }
  };

export const deleteRate = (rateId) => async (dispatch, getState) => {
  try {
    dispatch(deleteRateStart());
    const res = await axios.delete(url, {
      headers: { Authorization: `Bearer ${getState().auth.token}` },
      data: { id: rateId },
    });
    dispatch(deleteRateSuccess());
    return { error: false, id: res.data.response.id };
  } catch (err) {
    let errorObj = getErrorObjFormat(err);
    dispatch(deleteRateFailure(errorObj));
    return { error: true, ...errorObj };
  }
};

const fetchRateshopperAsyncForHotel = async ({
  date,
  hotelId,
  hotel_booking_url,
  token,
}) => {
  const startRes = await axios.post(
    `${url}/rateshopper-async/${date}/${hotelId}`,
    null,
    {
      headers: { Authorization: `Bearer ${token}` },
      ...(hotel_booking_url
        ? {
            params: {
              hotel_booking_url,
            },
          }
        : {}),
    }
  );

  if (startRes.data?.error || !startRes.data?.response?.status_url) {
    const err = new Error("Failed to start rateshopper job");
    err.response = {
      status: startRes.status,
      data: startRes.data,
    };
    throw err;
  }

  const statusUrl =
    process.env.REACT_APP_API_BASE_URL + startRes.data.response.status_url;
  await sleep(1000);

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const statusRes = await axios.get(statusUrl, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: () => true,
    });

    const data = statusRes.data;
    const jobStatus = data?.response?.status;

    if (!data?.error && jobStatus === "completed") {
      return data?.response?.result;
    }

    if (!data?.error && jobStatus === "running") {
      // Poll every 15 seconds to see if we have a result yet
      await sleep(1000 * 15);
      continue;
    }

    if (statusRes.status === 200 && !data?.error) {
      return data?.response?.result;
    }

    if (data?.error || statusRes.status >= 400) {
      const err = new Error("Rateshopper job failed");
      err.response = {
        status: statusRes.status,
        data,
      };
      throw err;
    }

    // Unknown non-terminal response, keep polling defensively.
    await sleep(1000 * 30);
  }
};

export const fetchRateshopper =
  (date, hotelList) => async (dispatch, getState) => {
    try {
      dispatch(getRateshopperStart());
      const token = getState().auth.token;

      const allResults = await Promise.all(
        hotelList.map(({ id: hotelId, hotel_booking_url }) =>
          fetchRateshopperAsyncForHotel({
            date,
            hotelId,
            hotel_booking_url,
            token,
          })
        )
      );

      const mapOfHotelRatesFormatted = hotelList.flatMap(({ id, name }, i) => {
        const result = allResults[i];
        if (result && result.rooms && result.rooms.length > 0) {
          return { id, name, rooms: result.rooms };
        }
        return [];
      });

      dispatch(
        getRateshopperSuccess({
          rateshopper: mapOfHotelRatesFormatted,
        })
      );
    } catch (err) {
      let errorObj = getErrorObjFormat(err);
      dispatch(getRateshopperFailure(errorObj));
      return { error: true, ...errorObj };
    }
  };

export const syncRateshopper = (ratesObj) => async (dispatch, getState) => {
  try {
    dispatch(syncRateshopperStart());
    const res = await axios.put(`${url}/live`, ratesObj, {
      headers: { Authorization: `Bearer ${getState().auth.token}` },
    });
    dispatch(syncRateshopperSuccess());
    return { error: false, res };
  } catch (err) {
    let errorObj = getErrorObjFormat(err);
    dispatch(syncRateshopperFailure(errorObj));
    return { error: true, ...errorObj };
  }
};
